import { useCallback, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { AxiosResponse } from 'axios';
import useSWR from 'swr';

import { signInModalInstance } from 'components/candidate/SignInModal';
import { useCommon } from 'hooks/useCommon';
import { CompaniesResponse, CompanyResponse, TCompany } from 'lib/models/company';
import { useAuth } from 'lib/providers/AuthProvider';
import { TOpportunity } from 'lib/models/opportunity';
import { API_ROUTES } from 'lib/api-routes';
import { apiInstance } from 'lib/utils/axios';
import { parseArrayResponse } from 'lib/utils/parser';

export const PER_PAGE_COUNT = 5;

export enum UserActionEnum {
  FOLLOW_COMPANY = 'follow_company',
  UNFOLLOW_COMPANY = 'unfollow_company',
}

type UpdateUserActionProps =
  | {
      userAction: UserActionEnum.FOLLOW_COMPANY | UserActionEnum.UNFOLLOW_COMPANY;
      userActionId: string;
    }
  | { userAction: null };

export function useCompanyInternalHook({
  company: staticPropsCompany,
  publishedJobs,
  publishedJobsTotalCount,
  publishedInternshipsTotalCount,
}: {
  company: TCompany;
  publishedJobs: Array<TOpportunity>;
  publishedJobsTotalCount: number;
  publishedInternshipsTotalCount: number;
}) {
  const { replace, query } = useRouter();
  const { isInitialAuthLoading, isCandidate, isGuestCandidate } = useAuth();
  const { followEntity, unfollowEntity } = useCommon();
  const { user_action: queryUserAction, user_action_id = null } = query;
  const queryUserActionId = user_action_id as string;
  const companyId = staticPropsCompany.id;
  const [isFollowLoading, setIsFollowLoading] = useState(false);

  const companyPath = `${API_ROUTES.COMPANY_LIST}/${companyId}`;
  const shouldFetchCompany = isCandidate;

  const companyFallbackData = useMemo(() => {
    return {
      data: { data: { id: companyId, type: 'company', attributes: { ...staticPropsCompany } } },
    } as AxiosResponse<CompanyResponse>;
  }, [staticPropsCompany]);

  const {
    data: companyResponse,
    isLoading: isCompanyLoading,
    mutate: mutateCompanyResponse,
  } = useSWR<AxiosResponse<CompanyResponse>>(shouldFetchCompany ? companyPath : null, apiInstance.get, {
    fallbackData: companyFallbackData,
    revalidateOnFocus: false,
  });

  const company = useMemo(() => {
    if (isCompanyLoading) return staticPropsCompany;
    return companyResponse?.data.data.attributes || staticPropsCompany;
  }, [isCompanyLoading, companyResponse, staticPropsCompany]);

  const { slug } = company;

  const similarCompaniesPathname = isInitialAuthLoading
    ? null
    : `${API_ROUTES.COMPANY_LIST}/${slug}${API_ROUTES.SIMILAR_COMPANIES}${
        isCandidate ? '/' : ''
      }?sort_by=updated_at&sort_order=desc&with_details=true&page=1&per_page=10`;
  const { data: similarCompaniesResponse, mutate: mutateSimilarCompaniesResponse } = useSWR<AxiosResponse<CompaniesResponse>>(
    similarCompaniesPathname,
    apiInstance.get,
    {
      revalidateOnFocus: false,
    }
  );

  const similarCompanies = useMemo(() => {
    return parseArrayResponse(similarCompaniesResponse?.data);
  }, [similarCompaniesResponse]);

  const updateCompanyFields = useCallback((fields: Partial<TCompany>) => {
    mutateCompanyResponse(
      (oldCompanyResponse) => {
        if (!oldCompanyResponse) return oldCompanyResponse;

        return {
          ...oldCompanyResponse,
          data: {
            ...oldCompanyResponse.data,
            data: {
              ...oldCompanyResponse.data.data,
              attributes: {
                ...oldCompanyResponse.data.data.attributes,
                ...fields,
              },
            },
          },
        };
      },
      { revalidate: false }
    );
  }, []);

  const updateSimilarCompanyFields = useCallback((fields: Partial<TCompany>) => {
    mutateSimilarCompaniesResponse(
      (oldSimilarCompaniesResponse) => {
        if (!oldSimilarCompaniesResponse) return oldSimilarCompaniesResponse;

        return {
          ...oldSimilarCompaniesResponse,
          data: {
            ...oldSimilarCompaniesResponse.data,
            data: oldSimilarCompaniesResponse.data.data.map((company) => {
              if (company.id === fields.id) {
                return {
                  ...company,
                  attributes: {
                    ...company.attributes,
                    ...fields,
                  },
                };
              }
              return company;
            }),
          },
        };
      },
      {
        revalidate: false,
      }
    );
  }, []);

  const updateUserAction = useCallback(
    (props: UpdateUserActionProps) => {
      const { userAction } = props;
      if ('userActionId' in props) {
        replace({ query: { ...query, user_action: userAction, user_action_id: props.userActionId } }, undefined, { shallow: true });
      } else {
        if (userAction) {
          replace({ query: { ...query, user_action: userAction } }, undefined, { shallow: true });
        } else {
          const { user_action: _, user_action_id: __, ...restQuery } = query;
          replace({ query: restQuery }, undefined, { shallow: true });
        }
      }
    },
    [replace, query]
  );

  const toggleFollowCompany = useCallback(async () => {
    switch (queryUserAction) {
      case UserActionEnum.FOLLOW_COMPANY: {
        const response = await followEntity({ followable_type: 'Company', followable_id: queryUserActionId });
        updateUserAction({ userAction: null });
        if (companyId === queryUserActionId) {
          updateCompanyFields({ following: response });
        } else {
          updateSimilarCompanyFields({ id: queryUserActionId, following: response });
        }
        break;
      }
      case UserActionEnum.UNFOLLOW_COMPANY: {
        await unfollowEntity({ id: queryUserActionId });
        updateUserAction({ userAction: null });
        if (company.following?.data?.attributes.id === queryUserActionId) {
          updateCompanyFields({ following: null });
        } else {
          const updatedCompany = similarCompanies.find((company) => company.following?.data?.id === queryUserActionId);
          if (updatedCompany) {
            updateSimilarCompanyFields({ id: updatedCompany.id, following: null });
          }
        }
        break;
      }
      default:
        break;
    }
  }, [
    similarCompanies,
    companyId,
    queryUserActionId,
    queryUserAction,
    updateUserAction,
    followEntity,
    unfollowEntity,
    updateCompanyFields,
    updateSimilarCompanyFields,
  ]);

  const handleToggleFollowingCompany = useCallback(async () => {
    try {
      if (isCandidate) {
        setIsFollowLoading(true);
        await toggleFollowCompany();
      } else {
        await signInModalInstance();
      }
    } catch (error) {
      console.error(error);
      updateUserAction({ userAction: null });
    } finally {
      setIsFollowLoading(false);
    }
  }, [isCandidate, toggleFollowCompany, updateUserAction, signInModalInstance]);

  useEffect(() => {
    if (isInitialAuthLoading) return;

    if (Object.values(UserActionEnum).includes(queryUserAction as UserActionEnum)) {
      switch (queryUserAction) {
        case UserActionEnum.FOLLOW_COMPANY:
        case UserActionEnum.UNFOLLOW_COMPANY:
          handleToggleFollowingCompany();
          break;
        default:
          break;
      }
    }
  }, [isInitialAuthLoading, isCandidate, queryUserAction]);

  const socialMediaLinks = useMemo(
    () =>
      [
        { icon: 'icon_linkedin', url: company.linkedin_url },
        { icon: 'icon_facebook', url: company.facebook_url },
        { icon: 'icon_x', url: company.twitter_url },
        { icon: 'icon_instagram', url: company.instagram_url },
      ]
        .filter(({ url }) => url)
        .sort((a, b) => {
          if (a.url && b.url) return 0;
          else if (a.url) return -1;
          else if (b.url) return 1;
          return 0;
        }),
    [company]
  );

  const showBanner = isGuestCandidate && publishedJobsTotalCount > 0;

  return {
    company,
    similarCompanies,
    socialMediaLinks,
    publishedJobs,
    publishedJobsTotalCount,
    publishedInternshipsTotalCount,
    isFollowLoading,
    showBanner,
    updateUserAction,
  };
}

export type UseCompanyReturnType = ReturnType<typeof useCompanyInternalHook>;
